import React, { useState, useEffect } from 'react'
import { api, formatDate } from '../../utils/api'
import CatalogBrandProposals from './CatalogBrandProposals'
import '../../styles/pages.css'

// 'money' routes the claims writer through the money parser (registry-driven).
const DATA_TYPES = ['text', 'number', 'measure', 'url', 'enum', 'money']
const CONTENT_CLASSES = ['fact', 'expressive']

/**
 * Attribute proposals: keys the extractor keeps seeing that aren't in the
 * registered vocabulary. Register puts one on the product grain (item-grain
 * keys are refused server-side); Reject hides it from future sweeps.
 */
function CatalogProposals() {
  const [proposals, setProposals] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [actionKey, setActionKey] = useState(null)

  // Register form (one open at a time, keyed by attribute_key)
  const [registering, setRegistering] = useState(null)
  const [dataType, setDataType] = useState('text')
  const [contentClass, setContentClass] = useState('fact')
  const [description, setDescription] = useState('')

  useEffect(() => {
    loadProposals()
  }, [])

  async function loadProposals() {
    setLoading(true)
    setError(null)
    try {
      const response = await api.get('/api/graph/review/proposals')
      setProposals(response.items || [])
    } catch (err) {
      console.error('Failed to load proposals:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  function openRegister(proposal) {
    setRegistering(proposal.attribute_key)
    setDataType('text')
    setContentClass('fact')
    setDescription('')
  }

  async function handleRegister(attributeKey) {
    setActionKey(attributeKey)
    setError(null)
    try {
      await api.post(`/api/graph/review/proposals/${encodeURIComponent(attributeKey)}/register`, {
        data_type: dataType,
        content_class: contentClass,
        description: description.trim()
      })
      setRegistering(null)
      loadProposals()
    } catch (err) {
      console.error('Failed to register attribute:', err)
      setError(err.message)
    } finally {
      setActionKey(null)
    }
  }

  async function handleReject(attributeKey) {
    setActionKey(attributeKey)
    setError(null)
    try {
      await api.post(`/api/graph/review/proposals/${encodeURIComponent(attributeKey)}/reject`)
      setRegistering(null)
      loadProposals()
    } catch (err) {
      console.error('Failed to reject proposal:', err)
      setError(err.message)
    } finally {
      setActionKey(null)
    }
  }

  return (
    <>
      <CatalogBrandProposals />

      {error && (
        <div className="card" style={{ background: 'var(--color-error-light)', padding: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
          <strong>Error:</strong> {error}
          <button
            type="button"
            className="btn btn-sm"
            style={{ marginLeft: 'var(--space-3)' }}
            onClick={() => setError(null)}
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Attribute proposals</h3>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-12)' }}>
            <div className="loading-spinner" />
          </div>
        ) : proposals.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-title">No Pending Proposals</div>
            <div className="empty-state-description">
              The extractor hasn't seen any unregistered attribute keys.
            </div>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Attribute Key</th>
                <th>Example Value</th>
                <th style={{ textAlign: 'center' }}>Seen</th>
                <th style={{ width: 110 }}>First Seen</th>
                <th style={{ width: 180, textAlign: 'right' }}></th>
              </tr>
            </thead>
            <tbody>
              {proposals.map(proposal => (
                <React.Fragment key={proposal.attribute_key}>
                  <tr>
                    <td style={{ fontWeight: 500 }}>{proposal.attribute_key}</td>
                    <td>
                      <code className="text-sm">{JSON.stringify(proposal.example_value)}</code>
                    </td>
                    <td style={{ textAlign: 'center' }}>{proposal.occurrence_count}</td>
                    <td className="text-muted text-sm">{formatDate(proposal.first_seen_at)}</td>
                    <td style={{ textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          onClick={() => openRegister(proposal)}
                          disabled={actionKey !== null}
                        >
                          Register…
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleReject(proposal.attribute_key)}
                          disabled={actionKey !== null}
                        >
                          {actionKey === proposal.attribute_key ? 'Working…' : 'Reject'}
                        </button>
                      </div>
                    </td>
                  </tr>
                  {registering === proposal.attribute_key && (
                    <tr>
                      <td colSpan={5} style={{ background: 'var(--color-bg-secondary)' }}>
                        <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-end', flexWrap: 'wrap', padding: 'var(--space-2)' }}>
                          <div className="filter-group" style={{ width: 140 }}>
                            <label className="filter-label">Data type</label>
                            <select className="form-input" value={dataType} onChange={e => setDataType(e.target.value)}>
                              {DATA_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                          </div>
                          <div className="filter-group" style={{ width: 140 }}>
                            <label className="filter-label">Content class</label>
                            <select className="form-input" value={contentClass} onChange={e => setContentClass(e.target.value)}>
                              {CONTENT_CLASSES.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                          </div>
                          <div className="filter-group" style={{ flex: 1, minWidth: 220 }}>
                            <label className="filter-label">Description</label>
                            <input
                              className="form-input"
                              type="text"
                              placeholder="What this attribute means…"
                              value={description}
                              onChange={e => setDescription(e.target.value)}
                            />
                          </div>
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={() => handleRegister(proposal.attribute_key)}
                            disabled={actionKey !== null || !description.trim()}
                          >
                            {actionKey === proposal.attribute_key ? 'Registering…' : 'Register'}
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm"
                            onClick={() => setRegistering(null)}
                          >
                            Cancel
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  )
}

export default CatalogProposals
